import { type ReactNode } from 'react';
import { ErrorBoundary, type ErrorBoundaryProps } from './ErrorBoundary';

interface WrapperProps {
  children: ReactNode;
  onError?: ErrorBoundaryProps['onError'];
}

/**
 * Global Error Boundary - Wraps the entire app
 * Shows full-screen error page when app crashes
 */
export function GlobalErrorBoundary({ children, onError }: WrapperProps) {
  return (
    <ErrorBoundary level="global" onError={onError}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Page Error Boundary - Wraps individual pages/routes
 * Shows inline error message while keeping nav intact
 */
export function PageErrorBoundary({ children, onError }: WrapperProps) {
  return (
    <ErrorBoundary level="page" onError={onError}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Component Error Boundary - Wraps specific components
 * Shows compact error state within component area
 */
export function ComponentErrorBoundary({ children, onError }: WrapperProps) {
  return (
    <ErrorBoundary level="component" onError={onError}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Feature Error Boundary - Wraps feature modules
 * Isolates feature failures from rest of app
 */
export function FeatureErrorBoundary({
  children,
  onError,
  featureName,
}: WrapperProps & { featureName?: string }) {
  const handleError: ErrorBoundaryProps['onError'] = (error, errorInfo) => {
    console.error(`Feature Error [${featureName || 'Unknown'}]:`, error);
    onError?.(error, errorInfo);
  };

  return (
    <ErrorBoundary level="page" onError={handleError} resetOnPropsChange>
      {children}
    </ErrorBoundary>
  );
}
